import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Plus, Settings, Mail, Loader2, AlertCircle, Wand2, ShieldAlert, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { boardsApi, mailApi, publicApi } from '../api/client';
import api from '../api/client';
import { useAuthStore } from '../stores/auth.store';
import AppShell from '../components/layout/AppShell';
import KanbanBoard from '../components/board/KanbanBoard';
import BoardEditor from '../components/board/BoardEditor';

interface Board {
  id: string;
  name: string;
  icon: string;
  isDefault: boolean;
  columns: any[];
}

interface Account {
  id: string;
  provider: string;
  providerEmail: string;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = (user as any)?.role === 'admin';
  const [activeBoard, setActiveBoard] = useState<string | null>(null);
  const [showBoardEditor, setShowBoardEditor] = useState(false);
  const [editingBoard, setEditingBoard] = useState<Board | null>(null);
  const [showOnlyWithTasks, setShowOnlyWithTasks] = useState(false); // Punkt 1: Task-Filter
  
  // Fetch boards
  const { 
    data: boardsData, 
    isLoading: boardsLoading,
    refetch: refetchBoards,
  } = useQuery({
    queryKey: ['boards'],
    queryFn: () => boardsApi.getBoards(),
    staleTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnWindowFocus: false, // Prevent duplicate refetches
  });
  
  // Fetch mail accounts
  const { data: accountsData } = useQuery({
    queryKey: ['mail-accounts'],
    queryFn: () => mailApi.getAccounts(),
    staleTime: 5 * 60 * 1000, // 5 minutes cache - accounts rarely change
    refetchOnWindowFocus: false,
  });
  
  // Fetch OAuth status (public endpoint) - App.tsx already fetches this, so use longer staleTime
  const { data: oauthStatus, isLoading: oauthLoading } = useQuery({
    queryKey: ['public-oauth-status'],
    queryFn: () => publicApi.getOAuthStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutes - rarely changes
    refetchOnWindowFocus: false,
  });
  
  // OAuth connect mutations
  const connectMicrosoftMutation = useMutation({
    mutationFn: async () => {
      const result = await api.post('auth/oauth/microsoft/init').json<{ url: string }>();
      return result;
    },
    onSuccess: (data: { url: string }) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      if (error?.response?.status === 500) {
        toast.error('Microsoft OAuth nicht konfiguriert');
      } else {
        toast.error('Fehler beim Verbinden mit Microsoft');
      }
    },
  });
  
  const connectGoogleMutation = useMutation({
    mutationFn: async () => {
      const result = await api.post('auth/oauth/google/init').json<{ url: string }>();
      return result;
    },
    onSuccess: (data: { url: string }) => {
      window.location.href = data.url;
    },
    onError: (error: any) => {
      if (error?.response?.status === 500) {
        toast.error('Google OAuth nicht konfiguriert');
      } else {
        toast.error('Fehler beim Verbinden mit Google');
      }
    },
  });
  
  const boards = (boardsData?.boards || []) as Board[];
  const accounts = (accountsData?.accounts || []) as Account[];
  
  // Set default active board
  useEffect(() => {
    if (!activeBoard && boards.length > 0) {
      const defaultBoard = boards.find(b => b.isDefault) || boards[0];
      setActiveBoard(defaultBoard.id);
    }
  }, [boards, activeBoard]);
  
  const currentBoard = boards.find(b => b.id === activeBoard);
  
  const handleEditBoard = (board: Board) => {
    setEditingBoard(board);
    setShowBoardEditor(true);
  };
  
  const handleCreateBoard = () => {
    setEditingBoard(null);
    setShowBoardEditor(true);
  };
  
  if (boardsLoading) {
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppShell>
    );
  }
  
  // No mail accounts connected
  if (accounts.length === 0) {
    const microsoftConfigured = oauthStatus?.microsoft ?? false;
    const googleConfigured = oauthStatus?.google ?? false;
    const anyConfigured = oauthStatus?.anyConfigured ?? false;
    
    return (
      <AppShell>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-h2 text-text-primary mb-2">
              Verbinde dein E-Mail-Konto
            </h2>
            <p className="text-text-muted mb-6">
              Um Kolumi nutzen zu können, verbinde zunächst dein Microsoft 365 oder Gmail Konto.
            </p>
            
            {/* Loading State */}
            {oauthLoading && (
              <div className="flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            )}
            
            {/* No OAuth configured - Show setup wizard hint */}
            {!oauthLoading && !anyConfigured && (
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 text-left">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-text-primary mb-1">
                      OAuth noch nicht eingerichtet
                    </p>
                    <p className="text-text-muted">
                      {isAdmin 
                        ? 'Du musst zuerst Microsoft 365 oder Google OAuth konfigurieren, bevor Benutzer ihre E-Mail-Konten verbinden können.'
                        : 'Ein Administrator muss zuerst OAuth konfigurieren, bevor du dein E-Mail-Konto verbinden kannst.'}
                    </p>
                  </div>
                </div>
                
                {isAdmin && (
                  <button
                    onClick={() => navigate('/setup')}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Wand2 className="w-5 h-5" />
                    Setup-Assistent starten
                  </button>
                )}
              </div>
            )}
            
            {/* OAuth configured - Show available providers */}
            {!oauthLoading && anyConfigured && (
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {microsoftConfigured && (
                  <button
                    onClick={() => connectMicrosoftMutation.mutate()}
                    disabled={connectMicrosoftMutation.isPending}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0078d4] text-white rounded-lg hover:bg-[#0078d4]/90 transition-colors disabled:opacity-50"
                  >
                    {connectMicrosoftMutation.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor">
                        <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z"/>
                      </svg>
                    )}
                    Microsoft 365
                  </button>
                )}
                {googleConfigured && (
                  <button
                    onClick={() => connectGoogleMutation.mutate()}
                    disabled={connectGoogleMutation.isPending}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    {connectGoogleMutation.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    )}
                    Gmail
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </AppShell>
    );
  }
  
  return (
    <AppShell>
      {/* Board Tabs */}
      <div className="border-b border-border-default">
        <div className="flex items-center gap-1 px-4 overflow-x-auto">
          {boards.map((board) => (
            <button
              key={board.id}
              onClick={() => setActiveBoard(board.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeBoard === board.id
                  ? 'border-primary text-text-primary'
                  : 'border-transparent text-text-muted hover:text-text-secondary'
              }`}
            >
              <span>{board.icon}</span>
              <span>{board.name}</span>
              {activeBoard === board.id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditBoard(board);
                  }}
                  className="ml-1 p-1 hover:bg-bg-surface-hover rounded"
                >
                  <Settings className="w-3.5 h-3.5" />
                </button>
              )}
            </button>
          ))}
          <button
            onClick={handleCreateBoard}
            className="flex items-center gap-1 px-3 py-3 text-sm text-text-muted hover:text-text-secondary transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Neu</span>
          </button>
          
          {/* Punkt 1: Task-Filter Toggle */}
          <div className="ml-auto flex items-center">
            <button
              onClick={() => setShowOnlyWithTasks(!showOnlyWithTasks)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                showOnlyWithTasks 
                  ? 'bg-primary/10 text-primary border border-primary/30' 
                  : 'text-text-muted hover:text-text-secondary hover:bg-bg-surface-hover'
              }`}
              title={showOnlyWithTasks ? 'Alle Mails anzeigen' : 'Nur Mails mit Aufgaben'}
            >
              <CheckSquare className="w-4 h-4" />
              <span className="hidden sm:inline">
                {showOnlyWithTasks ? 'Mit Aufgaben' : 'Aufgaben-Filter'}
              </span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Kanban Board */}
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        {currentBoard ? (
          <KanbanBoard board={currentBoard} accounts={accounts} showOnlyWithTasks={showOnlyWithTasks} />
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-text-muted">Kein Board ausgewählt</p>
          </div>
        )}
      </div>
      
      {/* Board Editor Modal */}
      {showBoardEditor && (
        <BoardEditor
          board={editingBoard}
          accounts={accounts}
          onClose={() => {
            setShowBoardEditor(false);
            setEditingBoard(null);
          }}
          onSave={() => {
            setShowBoardEditor(false);
            setEditingBoard(null);
            refetchBoards();
          }}
        />
      )}
    </AppShell>
  );
}
